// AIBadge — Article 50(1)/(2) in-app transparency indicator.
// Spec: Obsidian/MC/01 - Projects/MC ERP/06 - Modules/M99 - IT-Compliance/01 - Frameworks/EU AI Act/Article 50 Transparency.md
// Pattern A: every AI-generated content surface must render this component.

import { useId, useState } from 'react'

export interface AIBadgeProps {
  model?: string
  provider?: 'azure-openai' | 'aws-bedrock' | 'vertex' | 'azure-doc-intel'
  region?: string
  language?: 'de' | 'en' | 'fr' | 'tr'
  position?: 'inline' | 'corner'
  className?: string
}

const LABELS: Record<NonNullable<AIBadgeProps['language']>, string> = {
  de: 'KI-generiert',
  en: 'AI-generated',
  fr: 'Généré par IA',
  tr: 'Yapay zeka tarafından oluşturuldu',
}

const PROVIDER_LABELS: Record<NonNullable<AIBadgeProps['provider']>, string> = {
  'azure-openai':    'Azure OpenAI',
  'aws-bedrock':     'AWS Bedrock',
  'vertex':          'Google Vertex AI',
  'azure-doc-intel': 'Azure Document Intelligence',
}

/**
 * Renders an accessible "AI-generated" badge per EU AI Act Article 50.
 * Use `position="inline"` alongside text; `position="corner"` as an
 * absolutely-positioned overlay on a container (parent must have
 * `position: relative`).
 *
 * NEVER suppress this badge — per spec, the legal cost of under-disclosure
 * is up to €15 M. See Article 50 Transparency.md § "When the user can hide".
 */
export function AIBadge({
  model,
  provider,
  region = 'EU',
  language = 'de',
  position = 'inline',
  className = '',
}: AIBadgeProps) {
  const tooltipId = useId()
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const label = LABELS[language]
  const providerLabel = provider ? PROVIDER_LABELS[provider] : null

  const tooltipParts: string[] = []
  if (model) tooltipParts.push(model)
  if (providerLabel) tooltipParts.push(providerLabel)
  tooltipParts.push(region)
  const tooltipText = tooltipParts.join(' · ')

  const showTooltip = () => setTooltipVisible(true)
  const hideTooltip = () => setTooltipVisible(false)

  const positionCls =
    position === 'corner'
      ? 'absolute top-2 right-2 z-10'
      : 'inline-flex'

  return (
    <span
      role="status"
      aria-label={label}
      data-ai-drafted="true"
      className={`${positionCls} items-center gap-1 ${className}`}
    >
      {/* Badge pill */}
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-tertiary-container text-on-tertiary-container border border-tertiary/20 select-none">
        <span
          className="material-symbols-outlined !text-[12px] leading-none"
          aria-hidden="true"
        >
          smart_toy
        </span>
        {label}
      </span>

      {/* Info icon with tooltip — only shown when there is tooltip content */}
      {tooltipText && (
        <span className="relative inline-flex items-center">
          <button
            type="button"
            aria-describedby={tooltipId}
            aria-label={tooltipText}
            tabIndex={0}
            className="text-on-surface-variant hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full transition-colors"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
          >
            <span
              className="material-symbols-outlined !text-[14px] leading-none"
              aria-hidden="true"
            >
              info
            </span>
          </button>

          {/* Tooltip */}
          {tooltipVisible && (
            <span
              id={tooltipId}
              role="tooltip"
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 whitespace-nowrap rounded-md bg-surface-container-highest text-on-surface border border-outline-variant shadow-lg px-2.5 py-1.5 text-[11px] font-medium pointer-events-none"
            >
              {tooltipText}
            </span>
          )}
        </span>
      )}
    </span>
  )
}
